/**
 * Risk parameter labels and helpers aligned with standing-instruction / consent form terminology.
 */

export const RISK_PARAM_LABELS = {
  maximumTradesPerDay: 'Maximum Trades Per Day',
  maximumDollarAmountPerTrade: 'Maximum Dollar Amount Per Trade',
  maximumDailyDollarExposure: 'Maximum Daily Dollar Exposure',
  priceToleranceThreshold: 'Price Tolerance Threshold (%)',
  eligibleOrderTypes: 'Eligible Order Types',
  enablePriceChecks: 'Enable Price Checks',
  deniedTickers: 'Denied Tickers (max 3)',
  enableDeviationOverride: 'Enable Deviation Override Prompt',
  autoApproveTrades: 'Auto-Approve Trades',
  tradeSizeMultiplier: 'Trade Size Multiplier',
};

export const DEFAULT_MAX_DAILY_TRADES = 30;
export const DEFAULT_MAX_SINGLE_TRADE_AMOUNT = 5000;
export const DEFAULT_PRICE_TOLERANCE_THRESHOLD = 10;
export const DEFAULT_ELIGIBLE_ORDER_TYPES = 'both';
export const MAX_DENIED_TICKERS = 3;
/** Letters-only tickers; optional class suffix (.A / .B). No digits. */
export const DENIED_TICKER_PATTERN = /^[A-Z]{1,5}(\.[A-Z]{1,2})?$/;

/** Default multiplier when none is set (backend + new channel mappings). */
export const DEFAULT_TRADE_SIZE_MULTIPLIER = 1;
/** Selectable values above 1X when admin enables multiplier for the user. */
export const TRADE_SIZE_MULTIPLIER_OPTIONS = [2, 3, 4, 5];
/** Full dropdown list when multiplier is enabled (1X through 5X). */
export const ALL_TRADE_SIZE_MULTIPLIER_OPTIONS = [
  DEFAULT_TRADE_SIZE_MULTIPLIER,
  ...TRADE_SIZE_MULTIPLIER_OPTIONS,
];

/**
 * Parse stored multiplier; keeps 1X as the default. Values outside 1 or 2–5 fall back to 1.
 */
export function parseTradeSizeMultiplier(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) {
    return DEFAULT_TRADE_SIZE_MULTIPLIER;
  }
  if (n === 1 || TRADE_SIZE_MULTIPLIER_OPTIONS.includes(n)) {
    return n;
  }
  return DEFAULT_TRADE_SIZE_MULTIPLIER;
}

/** Value sent on channel mapping save — locked to 1X unless admin enabled multiplier. */
export function tradeSizeMultiplierForSave(multiplierEnabled, value) {
  if (!multiplierEnabled) {
    return DEFAULT_TRADE_SIZE_MULTIPLIER;
  }
  return parseTradeSizeMultiplier(value);
}

/** Map API mapping row into form state trade_size_multiplier. */
export function tradeSizeMultiplierFromMapping(mapping, multiplierEnabled) {
  if (!multiplierEnabled) {
    return DEFAULT_TRADE_SIZE_MULTIPLIER;
  }
  return parseTradeSizeMultiplier(
    mapping?.trade_size_multiplier ?? mapping?.position_multiplier ?? DEFAULT_TRADE_SIZE_MULTIPLIER
  );
}

/** Challenge tier → max dollar amount per trade (consent form schedule). */
export const CHALLENGE_TIER_MAX_TRADE_AMOUNTS = [
  { tierLabel: '200K Challenge', match: '200k', amount: 75000 },
  { tierLabel: '50K Challenge', match: '50k', amount: 25000 },
  { tierLabel: '25K Challenge', match: '25k', amount: 12500 },
  { tierLabel: '10K Challenge', match: '10k', amount: 5000 },
];

export const CHALLENGE_TIER_DOLLAR_LIMIT_HELP =
  'Auto-filled from your Discord channel (10K → $5,000; 25K → $12,500; 50K → $25,000; 200K → $75,000). Both fields use the same value per your standing instruction.';

export const ELIGIBLE_ORDER_TYPE_OPTIONS = [
  { value: 'both', label: 'Both (Market and Limit)' },
  { value: 'market', label: 'Market' },
  { value: 'limit', label: 'Limit' },
];

const VALID_ELIGIBLE_ORDER_TYPES = new Set(ELIGIBLE_ORDER_TYPE_OPTIONS.map((o) => o.value));

/**
 * Normalize channel name for tier matching (case-insensitive, ignore spaces/underscores/hyphens).
 */
export function normalizeChannelNameForTier(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

/**
 * Resolve display name for a channel id from the available-channels list.
 */
export function resolveChannelName(channelId, availableChannels = []) {
  if (!channelId) return '';
  const match = availableChannels.find((c) => c.channel_id === channelId);
  return match?.channel_name || channelId;
}

/**
 * Map challenge channel name to max dollar amount per trade, or null if tier is unknown.
 */
export function getMaxDollarAmountPerTradeForChannel(channelNameOrId, availableChannels = []) {
  const name =
    availableChannels.some((c) => c.channel_id === channelNameOrId)
      ? resolveChannelName(channelNameOrId, availableChannels)
      : channelNameOrId;
  const normalized = normalizeChannelNameForTier(name);
  if (!normalized) return null;

  for (const { match, amount } of CHALLENGE_TIER_MAX_TRADE_AMOUNTS) {
    if (normalized.includes(match)) return amount;
  }
  return null;
}

export function normalizeEligibleOrderTypes(value) {
  const v = String(value || DEFAULT_ELIGIBLE_ORDER_TYPES).toLowerCase();
  return VALID_ELIGIBLE_ORDER_TYPES.has(v) ? v : DEFAULT_ELIGIBLE_ORDER_TYPES;
}

export function createDefaultMappingFormState() {
  return {
    channel_id: '',
    trading_platform: 'tradier',
    account_id: '',
    api_token: '',
    discord_user_id: '',
    use_sandbox: false,
    trade_size_multiplier: DEFAULT_TRADE_SIZE_MULTIPLIER,
    max_daily_trades: DEFAULT_MAX_DAILY_TRADES,
    max_single_trade_amount: DEFAULT_MAX_SINGLE_TRADE_AMOUNT,
    max_daily_dollar_exposure: DEFAULT_MAX_SINGLE_TRADE_AMOUNT,
    price_deviation_limit: DEFAULT_PRICE_TOLERANCE_THRESHOLD,
    eligible_order_types: DEFAULT_ELIGIBLE_ORDER_TYPES,
    enable_price_checks: true,
    denied_tickers: '',
    enable_deviation_override: false,
    auto_approve_enabled: false,
  };
}

/**
 * Format API denied_tickers array for comma-separated form input.
 */
export function formatDeniedTickersForDisplay(list) {
  if (!list || !Array.isArray(list) || list.length === 0) {
    return '';
  }
  return list.map((t) => String(t).trim().toUpperCase()).filter(Boolean).join(', ');
}

/** Read-only summary for channel mapping cards (API snake_case or camelCase). */
export function deniedTickersSummaryForMapping(mapping) {
  const raw = mapping?.denied_tickers ?? mapping?.deniedTickers;
  if (Array.isArray(raw)) {
    const formatted = formatDeniedTickersForDisplay(raw);
    return formatted || 'None';
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }
  return 'None';
}

export function hasDeniedTickers(mapping) {
  return deniedTickersSummaryForMapping(mapping) !== 'None';
}

/**
 * Parse comma-separated denied tickers for API save (client-side validation).
 * Returns { tickers, error }.
 */
export function parseDeniedTickersForSave(raw) {
  const stripped = String(raw || '').trim();
  if (!stripped) {
    return { tickers: [], error: null };
  }
  const parts = stripped.split(',').map((p) => p.trim()).filter(Boolean);
  const normalized = [];
  const seen = new Set();
  for (const part of parts) {
    const token = part.toUpperCase();
    if (!DENIED_TICKER_PATTERN.test(token)) {
      return {
        tickers: [],
        error:
          `Invalid ticker "${part}": use 1–5 letters (optional .A/.B suffix, e.g. BRK.B). Numbers are not allowed.`,
      };
    }
    if (seen.has(token)) {
      continue;
    }
    seen.add(token);
    normalized.push(token);
  }
  if (normalized.length > MAX_DENIED_TICKERS) {
    return {
      tickers: [],
      error: `Denied ticker list allows at most ${MAX_DENIED_TICKERS} symbols.`,
    };
  }
  return { tickers: normalized, error: null };
}

export function deniedTickersForSave(raw) {
  const { tickers, error } = parseDeniedTickersForSave(raw);
  if (error) {
    throw new Error(error);
  }
  return tickers;
}

/**
 * Normalize a dollar cap input (per-trade and daily exposure share this value).
 */
export function syncMaxDollarLimitFields(amount) {
  if (amount === '' || amount == null) {
    return DEFAULT_MAX_SINGLE_TRADE_AMOUNT;
  }
  const value = Number(amount);
  if (!Number.isFinite(value) || value < 0) {
    return DEFAULT_MAX_SINGLE_TRADE_AMOUNT;
  }
  return value;
}

/** Keep both consent-aligned dollar fields on the same value. */
export function applySyncedDollarLimitsToForm(prev, amount) {
  const value = syncMaxDollarLimitFields(amount);
  return {
    ...prev,
    max_single_trade_amount: value,
    max_daily_dollar_exposure: value,
  };
}

/**
 * Apply channel-selection defaults (tier table) when a challenge channel is chosen.
 */
export function applyChannelSelectionToMappingForm(prev, channelId, availableChannels = []) {
  const next = { ...prev, channel_id: channelId };
  const tierAmount = getMaxDollarAmountPerTradeForChannel(channelId, availableChannels);
  if (tierAmount != null) {
    return applySyncedDollarLimitsToForm(next, tierAmount);
  }
  return next;
}

/** Resolve daily exposure from API mapping row (falls back to per-trade amount). */
export function resolveMaxDailyDollarExposure(mapping) {
  if (!mapping) return DEFAULT_MAX_SINGLE_TRADE_AMOUNT;
  const fromSettings = mapping.maximum_daily_dollar_exposure ?? mapping.max_daily_dollar_exposure;
  if (fromSettings != null && fromSettings !== '') {
    return syncMaxDollarLimitFields(fromSettings);
  }
  return syncMaxDollarLimitFields(mapping.max_single_trade_amount);
}
